'use client';

import { ContextualPopup, Table } from '@/ui';
import { useScrollingHandDetection } from '@/hooks/useScrollingHandDetection';
import type { FC, PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import { useDataPopup } from '../hooks/useDataPopup';
import { useTruth } from '../hooks/useTruth';
import { messages } from '../messages';

interface ConditionScrollingHandProps extends PropsWithChildren {
  right?: string;
  left?: string;
}

export const ConditionScrollingHand: FC<ConditionScrollingHandProps> = ({
  right,
  left,
  children,
}) => {
  const { dataBlocks } = useTruth();
  const { touchPosition } = dataBlocks;
  const { DataPopup } = useDataPopup();
  const { finalGuess, guesses, confidenceLow, confidenceHigh } =
    useScrollingHandDetection();

  const [handle, setHandle] = useState<React.ReactNode>(children);

  useEffect(() => {
    if (finalGuess === 'R') {
      setHandle(right);
    } else if (finalGuess === 'L') {
      setHandle(left);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- branch props are static JSX; keyed on the guess
  }, [finalGuess]);

  return (
    <DataPopup
      handle={handle}
      subheading={
        finalGuess ? '' : messages['condition.scrollingHand.noTouchScreen']
      }
      condition={{
        title: messages['condition.scrollingHand'],
        options: [
          {
            title: messages['condition.scrollingHand.right'],
            value: right,
          },
          {
            title: messages['condition.scrollingHand.left'],
            value: left,
          },
          {
            title: messages['condition.scrollingHand.unknown'],
            value: children?.toString() || '',
          },
        ],
      }}
      {...(finalGuess
        ? {
            dataVisualisation: (
              <ContextualPopup.Section>
                <Table.List
                  header={messages['condition.scrollingHand.guesses.title']}
                  items={
                    guesses.length > 4
                      ? [...guesses.slice(0, 4), `+ ${guesses.length - 4} more`]
                      : guesses
                  }
                />
              </ContextualPopup.Section>
            ),
            confidence: {
              rangeLow: confidenceLow,
              rangeHigh: confidenceHigh,
            },
            dataBlocks: { touchPosition },
          }
        : {})}
    />
  );
};
